import { useState } from 'react';
import type { GuaranteedServiceEventRecord } from '@nadoutong/contracts';
import type { I18nKey } from '../../i18n/index.ts';
import { useGuaranteedEvents } from '../../api/guaranteedService.ts';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { CustomerCell } from '../../components/identity/CustomerCell.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Chip,
  Pagination,
  Panel,
  Select,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tooltip,
  Tr,
} from '../../ui/index.ts';
import { detailsJson, eventChip, eventDetailLine } from './eventModel.ts';
import { triggerKey } from './legEvidence.ts';
import { physicalLegKey, useGuaranteedNameMaps, type GuaranteedNameMaps } from './lookups.ts';

const EVENT_KINDS = [
  'target_probe_trip',
  'target_probe_recover',
  'chain_trip_suppressed',
  'shadow_trip',
  'shadow_recover',
  'guaranteed_shadow_enter',
  'trip_skipped_immune',
  'target_probe_skip',
  'no_response_cutoff',
  'first_content_would_cut',
  'stall_cutoff',
  'probe_level',
  'probe_war_blocked',
  'jev_verdict',
  'judge_error',
] as const;

type EventKind = (typeof EVENT_KINDS)[number];

/** `trip_reason` values a trip / release can carry (probe-state reasons, then the guard kinds). */
const FAULT_KIND_FILTERS = [
  'probe', 'quality', 'chain', 'jev',
  'silent', 'broken', 'overload', 'first_content_stream', 'first_content_nonstream', 'prelude_fail',
] as const;

type FaultKindFilter = (typeof FAULT_KIND_FILTERS)[number];

function isFaultKindFilter(value: string): value is FaultKindFilter {
  return FAULT_KIND_FILTERS.some((kind) => kind === value);
}

function isEventKind(value: string): value is EventKind {
  return EVENT_KINDS.some((kind) => kind === value);
}

function kindLabel(kind: EventKind): I18nKey {
  switch (kind) {
    case 'target_probe_trip':
      return 'guaranteed.events.kind.target_probe_trip';
    case 'target_probe_recover':
      return 'guaranteed.events.kind.target_probe_recover';
    case 'chain_trip_suppressed':
      return 'guaranteed.events.kind.chain_trip_suppressed';
    case 'shadow_trip':
      return 'guaranteed.events.kind.shadow_trip';
    case 'shadow_recover':
      return 'guaranteed.events.kind.shadow_recover';
    case 'guaranteed_shadow_enter':
      return 'guaranteed.events.kind.guaranteed_shadow_enter';
    case 'trip_skipped_immune':
      return 'guaranteed.events.kind.trip_skipped_immune';
    case 'target_probe_skip':
      return 'guaranteed.events.kind.target_probe_skip';
    case 'no_response_cutoff':
      return 'guaranteed.events.kind.no_response_cutoff';
    case 'first_content_would_cut':
      return 'guaranteed.events.kind.first_content_would_cut';
    case 'stall_cutoff':
      return 'guaranteed.events.kind.stall_cutoff';
    case 'probe_level':
      return 'guaranteed.events.kind.probe_level';
    case 'probe_war_blocked':
      return 'guaranteed.events.kind.probe_war_blocked';
    case 'jev_verdict':
      return 'guaranteed.events.kind.jev_verdict';
    case 'judge_error':
      return 'guaranteed.events.kind.judge_error';
  }
}

function clockTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  const date = new Date(ms);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function EventsCard(): JSX.Element {
  const t = useT();
  const maps = useGuaranteedNameMaps();
  const [page, setPage] = useState(1);
  const [skuId, setSkuId] = useState('all');
  const [upstreamId, setUpstreamId] = useState('all');
  const [kind, setKind] = useState('all');
  const [faultKind, setFaultKind] = useState('all');
  const events = useGuaranteedEvents({
    page,
    skuId: skuId === 'all' ? '' : skuId,
    upstreamId: upstreamId === 'all' ? '' : upstreamId,
    kind: kind === 'all' ? '' : kind,
    faultKind: faultKind === 'all' ? '' : faultKind,
  });
  const total = events.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / 20));
  const rows = events.data?.events ?? [];

  return (
    <Panel
      testId="guaranteed-events-card"
      caption={(
        <span className="inline-flex items-baseline gap-2">
          <span>{t('guaranteed.section.events')}</span>
          <span className="text-label font-normal text-paper-low">
            {t('guaranteed.events.window', { count: String(total) })}
          </span>
        </span>
      )}
      actions={(
        <span className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-label text-paper-low">
            {t('guaranteed.events.sku')}
            <Select
              value={skuId}
              onValueChange={(value) => {
                setSkuId(value);
                setPage(1);
              }}
              options={[{ value: 'all', label: t('guaranteed.events.all_sku') }, ...maps.skuOptions]}
              ariaLabel={t('guaranteed.events.sku')}
              testId="guaranteed-events-sku"
            />
          </label>
          <label className="flex flex-col gap-1 text-label text-paper-low">
            {t('guaranteed.events.upstream')}
            <Select
              value={upstreamId}
              onValueChange={(value) => {
                setUpstreamId(value);
                setPage(1);
              }}
              options={[{ value: 'all', label: t('guaranteed.events.all_upstream') }, ...maps.upstreamOptions]}
              ariaLabel={t('guaranteed.events.upstream')}
              testId="guaranteed-events-upstream"
            />
          </label>
          <label className="flex flex-col gap-1 text-label text-paper-low">
            {t('guaranteed.events.kind')}
            <Select
              value={kind}
              onValueChange={(value) => {
                if (value === 'all' || isEventKind(value)) {
                  setKind(value);
                  setPage(1);
                  // A Jev verdict carries no fault kind.
                  if (value === 'jev_verdict') setFaultKind('all');
                }
              }}
              options={[
                { value: 'all', label: t('guaranteed.events.all_kind') },
                ...EVENT_KINDS.map((value) => ({ value, label: t(kindLabel(value)) })),
              ]}
              ariaLabel={t('guaranteed.events.kind')}
              testId="guaranteed-events-kind"
            />
          </label>
          {/* R1-B10: trips / releases of one fault kind (`trip_reason`). */}
          <label className="flex flex-col gap-1 text-label text-paper-low">
            {t('guaranteed.events.fault_kind')}
            <Select
              value={faultKind}
              onValueChange={(value) => {
                if (value === 'all' || isFaultKindFilter(value)) {
                  setFaultKind(value);
                  setPage(1);
                  if (kind === 'jev_verdict') setKind('all');
                }
              }}
              options={[
                { value: 'all', label: t('guaranteed.events.all_fault_kind') },
                ...FAULT_KIND_FILTERS.map((value) => ({ value, label: t(triggerKey(value)) })),
              ]}
              ariaLabel={t('guaranteed.events.fault_kind')}
              testId="guaranteed-events-fault-kind"
            />
          </label>
        </span>
      )}
    >
      <Table className="rounded-none border-0 bg-transparent" tableClassName="min-w-[960px]">
        <THead>
          <Tr interactive={false}>
            <Th>{t('guaranteed.events.col.time')}</Th>
            <Th>{t('guaranteed.events.col.kind')}</Th>
            <Th>{t('guaranteed.events.col.sku')}</Th>
            <Th>{t('guaranteed.events.col.upstream')}</Th>
            <Th>{t('guaranteed.events.col.customer')}</Th>
            <Th>{t('guaranteed.events.col.details')}</Th>
            <Th>{t('guaranteed.events.col.region')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.length === 0 ? (
            <TableEmpty colSpan={7}>{t('guaranteed.events.empty')}</TableEmpty>
          ) : rows.map((row, index) => (
            <EventRow
              key={`${row.event_ts}:${row.kind}:${String(index)}`}
              row={row}
              index={index}
              maps={maps}
            />
          ))}
        </TBody>
      </Table>
      <p className="m-0 px-3 py-2 text-micro text-paper-low" data-testid="guaranteed-events-shadow-note">
        {t('guaranteed.shadow_note')}
      </p>
      <Pagination
        className="px-3 py-2"
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
        prevLabel={t('common.previous')}
        nextLabel={t('common.next')}
        ariaLabel={t('guaranteed.section.events')}
        testId="guaranteed-events-pagination"
      />
    </Panel>
  );
}

function EventRow({ row, index, maps }: {
  readonly row: GuaranteedServiceEventRecord;
  readonly index: number;
  readonly maps: GuaranteedNameMaps;
}): JSX.Element {
  const t = useT();
  const chip = eventChip(row.kind, row.details);
  const line = eventDetailLine(row.details, t, row.kind);
  const json = detailsJson(row.details);
  const skuName = row.sku_id === null ? '—' : (maps.skuNameById.get(row.sku_id) ?? row.sku_id);
  const legKey = physicalLegKey(row);
  const legName = legKey === null ? undefined : maps.legByPhysicalKey.get(legKey);
  const upstream = row.upstream_name ?? legName?.upstream_name ?? row.upstream_id ?? '—';
  const model = legName?.physical_model ?? null;
  const org = row.org_id === null ? undefined : maps.orgNameById.get(row.org_id);
  const orgLabel = org?.org_name ?? row.org_id ?? '—';
  const orgSub = row.org_id === null ? '' : (org === undefined ? row.org_id : `${row.org_id} · ${org.email}`);
  return (
    <Tr data-testid={`guaranteed-event-${String(index)}`}>
      <Td mono className="whitespace-nowrap" title={formatLocalTimestamp(row.event_ts, { seconds: true })}>
        {clockTime(row.event_ts)}
      </Td>
      <Td>
        <Chip
          tone={chip.tone}
          lamp={false}
          testId={`guaranteed-event-chip-${String(index)}`}
          data-tone={chip.tone}
        >
          {chip.labelKey === null ? row.kind : t(chip.labelKey)}
        </Chip>
      </Td>
      <Td><span className="block max-w-[220px] truncate" title={skuName}>{skuName}</span></Td>
      <Td>
        <span className="block truncate font-medium text-paper" title={upstream}>{upstream}</span>
        {model === null ? null : (
          <span className="block truncate font-mono text-micro text-paper-low" title={model}>{model}</span>
        )}
      </Td>
      <Td>
        <CustomerCell
          orgId={row.org_id}
          testId={`guaranteed-event-customer-${String(index)}`}
          secondary={
            <div>
              <span className="block truncate font-medium text-paper" title={orgLabel}>{orgLabel}</span>
              {orgSub.length === 0 ? null : (
                <span className="block truncate font-mono text-micro text-paper-low" title={orgSub}>{orgSub}</span>
              )}
            </div>
          }
        />
      </Td>
      <Td>
        <Tooltip content={<pre className="m-0 max-w-sm whitespace-pre-wrap font-mono text-micro">{json}</pre>}>
          <span className="block max-w-xs truncate" title={json}>{line}</span>
        </Tooltip>
      </Td>
      <Td>{row.region}</Td>
    </Tr>
  );
}
